import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Key, 
  Webhook as WebhookIcon, 
  FileText, 
  Copy, 
  Plus, 
  Trash2, 
  Eye, 
  Check,
  AlertCircle,
  Clock,
  Activity,
  Globe,
  Play,
  X
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { developerApi, APIKey, Webhook, WebhookLog, generateWebhookURL, generateCurlExample, generatePythonExample, generateNodeExample } from '../services/developerApi';
import { workflowApi } from '../services/api';
// 简化的工作流类型，用于开发者页面
interface SimpleWorkflow {
  id: string;
  name: string;
  description?: string;
  status?: string;
}

// Tab 类型
type TabType = 'apikeys' | 'webhooks' | 'logs' | 'docs';

// 代码示例语言
type CodeLanguage = 'curl' | 'python' | 'nodejs';

export function DeveloperPage() {
  const [activeTab, setActiveTab] = useState<TabType>('apikeys');
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('developer.title')}</h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('developer.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <TabButton
              active={activeTab === 'apikeys'}
              onClick={() => setActiveTab('apikeys')}
              icon={<Key className="w-4 h-4" />}
              label={t('developer.tabs.apiKeys')}
            />
            <TabButton
              active={activeTab === 'webhooks'}
              onClick={() => setActiveTab('webhooks')}
              icon={<WebhookIcon className="w-4 h-4" />}
              label={t('developer.tabs.webhooks')}
            />
            <TabButton
              active={activeTab === 'logs'}
              onClick={() => setActiveTab('logs')}
              icon={<Activity className="w-4 h-4" />}
              label={t('developer.tabs.logs')}
            />
            <TabButton
              active={activeTab === 'docs'}
              onClick={() => setActiveTab('docs')}
              icon={<FileText className="w-4 h-4" />}
              label={t('developer.tabs.docs')}
            />
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'apikeys' && <APIKeysTab />}
        {activeTab === 'webhooks' && <WebhooksTab />}
        {activeTab === 'logs' && <LogsTab />}
        {activeTab === 'docs' && <DocsTab />}
      </div>
    </div>
  );
}

// Tab 按钮组件
function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
        active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// API Keys Tab
function APIKeysTab() {
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      const response = await developerApi.listAPIKeys();
      setApiKeys(response.data.keys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    
    try {
      const response = await developerApi.createAPIKey({ name: newKeyName });
      setNewKey(response.data.fullKey);
      loadAPIKeys();
    } catch (error) {
      console.error('Failed to create API key:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('developer.apiKeys.deleteConfirm'))) return;
    
    try {
      await developerApi.deleteAPIKey(id);
      loadAPIKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setNewKeyName('');
    setNewKey(null);
  };

  if (isLoading) {
    return <div className="text-center py-12">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('developer.apiKeys.title')}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('developer.apiKeys.subtitle')}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('developer.apiKeys.create')}
        </Button>
      </div>

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <Card className="p-12 text-center">
          <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('developer.apiKeys.emptyTitle')}</h3>
          <p className="text-gray-500 mb-4">{t('developer.apiKeys.emptyDesc')}</p>
          <Button onClick={() => setShowCreateModal(true)}>{t('developer.apiKeys.create')}</Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <Card key={key.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-900">{key.name}</h3>
                    <StatusBadge status={key.status} />
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                    <span className="font-mono">{key.keyPrefix}...</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {t('developer.apiKeys.createdOn', { date: new Date(key.createdAt).toLocaleDateString() })}
                    </span>
                    {key.lastUsedAt && (
                      <span className="flex items-center gap-1">
                        <Activity className="w-3 h-3" />
                        {t('developer.apiKeys.lastUsedOn', { date: new Date(key.lastUsedAt).toLocaleDateString() })}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(key.id)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('developer.apiKeys.createModal.title')}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {newKey ? (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          {t('developer.apiKeys.createModal.copyNowTitle')}
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          {t('developer.apiKeys.createModal.copyNowDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
                      {newKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newKey)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                  <Button onClick={closeModal} className="w-full">
                    {t('common.done')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('developer.apiKeys.createModal.keyName')}
                    </label>
                    <Input
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder={t('developer.apiKeys.createModal.keyNamePlaceholder')}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={closeModal} className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200">
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleCreate} disabled={!newKeyName.trim()} className="flex-1">
                      {t('common.create')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Webhooks Tab
function WebhooksTab() {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [workflows, setWorkflows] = useState<SimpleWorkflow[]>([]);
  const [baseUrl, setBaseUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [webhooksRes, workflowsRes] = await Promise.all([
        developerApi.listWebhooks(),
        workflowApi.listWorkflows(),
      ]);
      setWebhooks(webhooksRes.data.webhooks);
      setBaseUrl(webhooksRes.data.baseUrl);
      setWorkflows(workflowsRes.data.workflows as SimpleWorkflow[]);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('developer.webhooks.deleteConfirm'))) return;
    
    try {
      await developerApi.deleteWebhook(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete webhook:', error);
    }
  };

  const getWorkflowName = (workflowId: string) => {
    const workflow = workflows.find(w => w.id === workflowId);
    return workflow?.name || workflowId;
  };

  if (isLoading) {
    return <div className="text-center py-12">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('developer.webhooks.title')}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('developer.webhooks.subtitle')}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('developer.webhooks.create')}
        </Button>
      </div>

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <Card className="p-12 text-center">
          <WebhookIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('developer.webhooks.emptyTitle')}</h3>
          <p className="text-gray-500 mb-4">{t('developer.webhooks.emptyDesc')}</p>
          <Button onClick={() => setShowCreateModal(true)}>{t('developer.webhooks.create')}</Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-900">{webhook.name}</h3>
                    <StatusBadge status={webhook.status} />
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                      {t(`developer.logs.direction.${webhook.triggerType}`)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('developer.webhooks.workflowPrefix')} {getWorkflowName(webhook.workflowId)}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                      {generateWebhookURL(baseUrl, webhook.urlToken)}
                    </code>
                    <button
                      onClick={() => copyToClipboard(generateWebhookURL(baseUrl, webhook.urlToken))}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                    <span>{webhook.method}</span>
                    <span>{t('developer.webhooks.calls', { count: webhook.callCount })}</span>
                    {webhook.lastCalledAt && (
                      <span>{t('developer.webhooks.lastCalledOn', { date: new Date(webhook.lastCalledAt).toLocaleDateString() })}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedWebhook(webhook)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(webhook.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateWebhookModal
          workflows={workflows}
          onClose={() => setShowCreateModal(false)}
          onCreated={loadData}
        />
      )}

      {/* Detail Modal */}
      {selectedWebhook && (
        <WebhookDetailModal
          webhook={selectedWebhook}
          baseUrl={baseUrl}
          workflowName={getWorkflowName((selectedWebhook as Webhook).workflowId)}
          onClose={() => setSelectedWebhook(null)}
        />
      )}
    </div>
  );
}

// Create Webhook Modal
function CreateWebhookModal({ workflows, onClose, onCreated }: { workflows: SimpleWorkflow[]; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !workflowId) return;
    
    setIsSubmitting(true);
    try {
      await developerApi.createWebhook({ name, workflowId });
      onCreated();
      onClose();
    } catch (error) {
      console.error('Failed to create webhook:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('developer.webhooks.createModal.title')}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('developer.webhooks.createModal.name')}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('developer.webhooks.createModal.namePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('developer.webhooks.createModal.workflow')}
              </label>
              <select
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('developer.webhooks.createModal.selectWorkflow')}</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200">
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!name.trim() || !workflowId || isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? t('developer.webhooks.createModal.creating') : t('common.create')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Webhook Detail Modal
function WebhookDetailModal({ webhook, baseUrl, workflowName, onClose }: { webhook: Webhook; baseUrl: string; workflowName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<CodeLanguage>('curl');
  const webhookUrl = generateWebhookURL(baseUrl, webhook.urlToken);

  const getExampleCode = () => {
    switch (activeLang) {
      case 'curl':
        return generateCurlExample(webhookUrl, webhook.secret);
      case 'python':
        return generatePythonExample(webhookUrl, webhook.secret);
      case 'nodejs':
        return generateNodeExample(webhookUrl, webhook.secret);
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('developer.webhooks.detail.title')}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">{t('developer.webhooks.detail.name')}</label>
                <p className="text-sm text-gray-900">{webhook.name}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">{t('developer.webhooks.detail.status')}</label>
                <p><StatusBadge status={webhook.status} /></p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">{t('developer.webhooks.detail.workflow')}</label>
                <p className="text-sm text-gray-900">{workflowName}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">{t('developer.webhooks.detail.method')}</label>
                <p className="text-sm text-gray-900">{webhook.method}</p>
              </div>
            </div>

            {/* Webhook URL */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">{t('developer.webhooks.detail.url')}</label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
                  {webhookUrl}
                </code>
                <button
                  onClick={() => copyToClipboard(webhookUrl)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Code Examples */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">{t('developer.webhooks.detail.integrationExamples')}</label>
              <div className="flex gap-2 mb-3">
                <CodeLangButton active={activeLang === 'curl'} onClick={() => setActiveLang('curl')} label="cURL" />
                <CodeLangButton active={activeLang === 'python'} onClick={() => setActiveLang('python')} label="Python" />
                <CodeLangButton active={activeLang === 'nodejs'} onClick={() => setActiveLang('nodejs')} label="Node.js" />
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{getExampleCode()}</code>
              </pre>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-2xl font-semibold text-gray-900">{webhook.callCount}</p>
                <p className="text-xs text-gray-500">{t('developer.webhooks.detail.totalCalls')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold text-gray-900">{webhook.retryCount}</p>
                <p className="text-xs text-gray-500">{t('developer.webhooks.detail.retryCount')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold text-gray-900">{webhook.timeout}</p>
                <p className="text-xs text-gray-500">{t('developer.webhooks.detail.timeout')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Logs Tab
function LogsTab() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const response = await developerApi.getAllLogs(100);
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('developer.logs.title')}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('developer.logs.subtitle')}
        </p>
      </div>

      {logs.length === 0 ? (
        <Card className="p-12 text-center">
          <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('developer.logs.emptyTitle')}</h3>
          <p className="text-gray-500">{t('developer.logs.emptyDesc')}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <Card key={log.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      log.status === 'success' ? 'bg-green-100 text-green-700' :
                      log.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {t(`developer.logs.status.${log.status}`)}
                    </span>
                    <span className="text-sm font-medium text-gray-600">{log.method}</span>
                    <span className="text-sm text-gray-500">{t(`developer.logs.direction.${log.direction}`)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">{log.url}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span>{t('developer.logs.statusCode', { code: log.statusCode })}</span>
                    <span>{t('developer.logs.duration', { ms: log.duration })}</span>
                    <span>{new Date(log.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Docs Tab
function DocsTab() {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('developer.docs.title')}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('developer.docs.subtitle')}
        </p>
      </div>

      {/* Quick Start */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Play className="w-5 h-5 text-blue-500" />
          {t('developer.docs.quickStart')}
        </h3>
        <ol className="space-y-3 text-sm text-gray-600 list-decimal list-inside">
          <li>{t('developer.docs.quickStartSteps.step1')}</li>
          <li>{t('developer.docs.quickStartSteps.step2')}</li>
          <li>{t('developer.docs.quickStartSteps.step3')}</li>
          <li>{t('developer.docs.quickStartSteps.step4')}</li>
        </ol>
      </Card>

      {/* API Endpoints */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          {t('developer.docs.endpoints')}
        </h3>
        <div className="space-y-4">
          <EndpointDoc
            method="POST"
            path="/api/public/v1/workflow/run"
            description={t('developer.docs.endpointDescriptions.runWorkflow')}
          />
          <EndpointDoc
            method="GET"
            path="/api/public/v1/workflow/run/{runId}"
            description={t('developer.docs.endpointDescriptions.runStatus')}
          />
          <EndpointDoc
            method="GET"
            path="/api/public/v1/workflow/run/{runId}/result"
            description={t('developer.docs.endpointDescriptions.runResult')}
          />
          <EndpointDoc
            method="POST"
            path="/api/public/v1/workflow/upload-and-run"
            description={t('developer.docs.endpointDescriptions.uploadAndRun')}
          />
        </div>
      </Card>

      {/* Webhook Integration */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <WebhookIcon className="w-5 h-5 text-blue-500" />
          {t('developer.docs.webhookIntegration')}
        </h3>
        <div className="space-y-4 text-sm text-gray-600">
          <p>
            {t('developer.docs.webhookIntegrationDesc')}
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">{t('developer.docs.webhookUrlFormat')}</h4>
            <code className="text-sm bg-gray-100 px-2 py-1 rounded">
              POST /webhook/flow/&#123;token&#125;
            </code>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">{t('developer.docs.security')}</h4>
            <p>
              {t('developer.docs.securityDesc')}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Helper Components

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-700',
    revoked: 'bg-red-100 text-red-700',
    error: 'bg-red-100 text-red-700',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || colors.inactive}`}>
      {t(`developer.status.${status}`, { defaultValue: status })}
    </span>
  );
}

function CodeLangButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-sm rounded ${
        active ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

function EndpointDoc({ method, path, description }: { method: string; path: string; description: string }) {
  const methodColors: Record<string, string> = {
    GET: 'text-green-600 bg-green-50',
    POST: 'text-blue-600 bg-blue-50',
    PUT: 'text-yellow-600 bg-yellow-50',
    DELETE: 'text-red-600 bg-red-50',
  };

  return (
    <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
      <span className={`px-2 py-1 text-xs font-medium rounded ${methodColors[method] || 'text-gray-600 bg-gray-100'}`}>
        {method}
      </span>
      <div>
        <code className="text-sm font-mono text-gray-900">{path}</code>
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      </div>
    </div>
  );
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export default DeveloperPage;
